import { useState } from 'react'
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'
import {
  Button as AriaButton,
  Popover,
  Dialog,
  DialogTrigger,
  Calendar,
  CalendarGrid,
  CalendarCell,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  Heading,
  Label,
  Text,
} from 'react-aria-components'
import { CalendarDate, parseDate } from '@internationalized/date'
import { format, parse, isValid } from 'date-fns'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from '@untitledui-icons/react/line'
import { cx, sortCx } from '@/lib/utils/cx'
import { Button } from '@/components/base/buttons'

export const styles = sortCx({
  common: {
    root: 'flex w-full flex-col gap-1.5',
    label: 'text-sm font-medium text-fg-secondary',
    labelRequired: 'ml-0.5 text-fg-error-secondary',
    trigger: [
      'inline-flex h-10 w-full items-center justify-start gap-2 rounded-lg border bg-white px-3.5 text-left text-md shadow-xs transition-colors dark:bg-gray-900',
      'border-border-primary',
      'hover:border-border-brand hover:bg-gray-50 dark:hover:bg-gray-800',
      'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/10',
    ].join(' '),
    triggerError:
      'border-fg-error-secondary focus-visible:border-fg-error-secondary focus-visible:ring-error-500/10',
    triggerDisabled: 'cursor-not-allowed bg-bg-disabled_subtle text-fg-disabled hover:border-border-primary hover:bg-bg-disabled_subtle',
    triggerPlaceholder: 'text-fg-quaternary',
    triggerIcon: 'size-5 shrink-0 text-fg-quaternary',
    triggerText: 'truncate text-fg-primary',
    popover: [
      'rounded-xl border border-border-secondary bg-white p-4 shadow-lg dark:bg-gray-900',
      'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
      'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
    ].join(' '),
    dialog: 'outline-none',
    calendar: 'w-fit bg-white dark:bg-gray-900',
    calendarHeader: 'mb-4 flex items-center justify-between',
    navButton: 'rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800',
    navIcon: 'size-5 text-fg-tertiary',
    calendarHeading: 'text-sm font-semibold text-fg-primary',
    weekdayHeader: 'size-10 text-center text-sm font-medium text-fg-tertiary',
    calendarCell: [
      'flex size-10 cursor-pointer items-center justify-center rounded-full text-sm outline-none transition-colors',
      'hover:bg-gray-100 dark:hover:bg-gray-800',
      'focus-visible:ring-2 focus-visible:ring-brand-500',
      'selected:bg-brand-600 selected:text-white selected:hover:bg-brand-700',
      'today:font-semibold today:text-brand-600',
      'outside-visible-range:text-fg-disabled outside-visible-range:hover:bg-transparent',
      'disabled:cursor-not-allowed disabled:text-fg-disabled disabled:hover:bg-transparent',
    ].join(' '),
    description: 'text-sm text-fg-tertiary',
    error: 'text-sm text-fg-error-primary',
  },
})

export interface FormDatePickerProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  description?: string
  placeholder?: string
  isDisabled?: boolean
  isRequired?: boolean
  minValue?: Date
  maxValue?: Date
  className?: string
}

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToISOString(date: CalendarDate): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

export function FormDatePicker<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder = 'dd / mm / yyyy',
  isDisabled,
  isRequired,
  minValue,
  maxValue,
  className,
}: FormDatePickerProps<TFieldValues>) {
  const [isOpen, setIsOpen] = useState(false)

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  const value = field.value ? parseDate(field.value as string) : undefined

  const handleChange = (date: CalendarDate | null) => {
    if (date) {
      field.onChange(calendarDateToISOString(date))
    } else {
      field.onChange('')
    }
    setIsOpen(false)
  }

  // Format the display value
  const getDisplayValue = () => {
    if (!field.value) return null
    try {
      const parsed = parse(field.value as string, 'yyyy-MM-dd', new Date())
      if (isValid(parsed)) {
        return format(parsed, 'dd / MM / yyyy')
      }
    } catch {
      // Invalid date format
    }
    return null
  }

  const displayValue = getDisplayValue()

  return (
    <div className={cx(styles.common.root, className)}>
      {label && (
        <Label className={styles.common.label}>
          {label}
          {isRequired && <span className={styles.common.labelRequired}>*</span>}
        </Label>
      )}

      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <AriaButton
          className={cx(
            styles.common.trigger,
            error && styles.common.triggerError,
            isDisabled && styles.common.triggerDisabled
          )}
          isDisabled={isDisabled}
        >
          <CalendarIcon className={styles.common.triggerIcon} />
          {displayValue ? (
            <span className={styles.common.triggerText}>{displayValue}</span>
          ) : (
            <span className={styles.common.triggerPlaceholder}>{placeholder}</span>
          )}
        </AriaButton>

        <Popover placement="bottom start" className={styles.common.popover}>
          <Dialog className={styles.common.dialog}>
            <Calendar
              className={styles.common.calendar}
              value={value}
              onChange={handleChange}
              minValue={minValue ? dateToCalendarDate(minValue) : undefined}
              maxValue={maxValue ? dateToCalendarDate(maxValue) : undefined}
            >
              <header className={styles.common.calendarHeader}>
                <Button slot="previous" color="tertiary" size="sm" iconLeading={ChevronLeftIcon} />
                <Heading className={styles.common.calendarHeading} />
                <Button slot="next" color="tertiary" size="sm" iconLeading={ChevronRightIcon} />
              </header>
              <CalendarGrid>
                <CalendarGridHeader>
                  {(day) => (
                    <CalendarHeaderCell className={styles.common.weekdayHeader}>
                      {day}
                    </CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => (
                    <CalendarCell date={date} className={styles.common.calendarCell} />
                  )}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>
      </DialogTrigger>

      {description && !error && (
        <Text slot="description" className={styles.common.description}>
          {description}
        </Text>
      )}
      {error && (
        <Text slot="errorMessage" className={styles.common.error}>
          {error.message}
        </Text>
      )}
    </div>
  )
}

export default FormDatePicker
